import { useEffect } from 'react';

/**
 * Keeps a CSS custom property in sync with the *visible* viewport height
 * (`window.visualViewport.height`), so a layout can size itself to the area
 * that isn't covered by the on-screen keyboard.
 *
 * Why this exists: `100dvh` measures the full viewport, not the space left
 * above a soft keyboard. On the Capacitor **Android** shell the WebView does
 * not resize when the keyboard opens, so a `100dvh` chat layout pushes its
 * composer down behind the keyboard where it can't be seen or used (#1477).
 * `visualViewport` reports the real visible height on every platform, so
 * driving the height from it fixes Android without regressing iOS or web:
 * when no keyboard is up (or the WebView already resized, as on iOS), the
 * value equals the full height and nothing changes.
 *
 * The property is set on `<html>` so it can feed the whole height chain
 * (including the `.less`-scoped `html/body/#root` rules). It is removed on
 * unmount so it only affects the page that opts in.
 *
 * SSR-safe: all access to `window` happens inside the effect.
 */
export function useVisualViewportHeight(varName = '--support-vh'): void {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const root = document.documentElement;
    const vv = window.visualViewport;

    const sync = () => {
      const height = vv?.height ?? window.innerHeight;
      root.style.setProperty(varName, `${Math.round(height)}px`);
    };

    // A single read can hold a stale height in two cases, so re-read across the
    // next frame and shortly after (the trailing `resize`/`scroll` events fire
    // too, but this guarantees a correction even if they don't):
    //  - `orientationchange`: many WebViews still report the pre-rotation
    //    (portrait) height at the instant the event fires (#1516).
    //  - Initial mount: on a push deep-link the chat mounts and its composer
    //    auto-focuses at the same time, so the keyboard rises together with the
    //    first paint and that first frame is mis-sized (the input is pushed up
    //    with a large gap above the keyboard) until the layout settles (#1516,
    //    #1566). The 300ms tail covers the iOS keyboard-open animation.
    let rafId = 0;
    let timeoutId = 0;
    const syncSettled = () => {
      sync();
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(sync);
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(sync, 300);
    };

    syncSettled();

    // `resize` covers keyboard open/close and browser-chrome changes; `scroll`
    // keeps us correct if the visual viewport is panned while the keyboard is up.
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', syncSettled);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', syncSettled);
      root.style.removeProperty(varName);
    };
  }, [varName]);
}
